/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { useCallback } from 'react';
import * as React from 'react';
import { Link } from 'react-router';

import { Typography2 } from '@coursera/cds-core';
import { ChevronDownIcon } from '@coursera/cds-icons';

import { useSideNavCollapse } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';
import { SideNavToolTipWrapper } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavToolTipWrapper';
import type { SideNavItem as SideNavItemType } from 'bundles/enterprise-admin-navigation/components/side-nav/base/types';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  item: css`
    padding: 0;
    margin: 0;
    list-style: none;
  `,
  parentButton: css`
    width: 100%;
    height: 36px;
    display: flex;
    align-items: center;
    padding: 0 var(--cds-spacing-100);
    background-color: transparent;
    border: none;
    border-radius: var(--cds-border-radius-100);
    cursor: pointer;
    transition:
      background-color 200ms ease,
      color 200ms ease,
      font-weight 200ms ease,
      width 200ms ease,
      padding 200ms ease;

    /* && doubles the class to beat the global .styleguide a:hover { text-decoration: underline } rule */
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:active {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);

      & svg {
        color: var(--cds-color-neutral-primary-weak);
      }
    }

    /* :focus-visible (not :focus) so the hover background doesn't stick after a mouse click */
    &&:hover,
    &&:focus-visible {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
    }
  `,
  parentButtonCollapsed: css`
    width: 36px;
    margin-left: var(--cds-spacing-50);
    justify-content: center;
    padding: 0 var(--cds-spacing-100);
  `,
  parentButtonActive: css`
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:focus-visible,
    &&:active {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
      color: var(--cds-color-neutral-primary);

      & svg {
        color: var(--cds-color-neutral-primary);
      }
    }
  `,
  icon: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: inherit;

    & svg {
      color: inherit;
    }
  `,
  label: css`
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition:
      opacity 200ms ease,
      width 200ms ease,
      margin 200ms ease;
    opacity: 1;
    width: auto;
    margin-left: var(--cds-spacing-150);
  `,
  labelHidden: css`
    opacity: 0;
    width: 0;
    margin-left: 0;
  `,
  chevron: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    transition:
      transform 200ms ease,
      opacity 200ms ease,
      width 200ms ease,
      margin 200ms ease;
    opacity: 1;
    margin-left: auto;
  `,
  chevronHidden: css`
    opacity: 0;
    width: 0;
    margin-left: 0;
  `,
  chevronOpen: css`
    transform: rotate(0deg);
  `,
  chevronClosed: css`
    transform: rotate(-90deg);
  `,
  submenu: css`
    list-style: none;
    padding: 0;
    margin: var(--cds-spacing-50) 0;
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  submenuHidden: css`
    display: none;
  `,
  subitemLink: css`
    display: flex;
    align-items: center;
    height: 36px;
    padding: var(--cds-spacing-100);
    border-radius: var(--cds-border-radius-100);
    transition:
      background-color 200ms ease,
      color 200ms ease;

    /* && doubles the class to beat the global .styleguide a:hover { text-decoration: underline } rule */
    &&,
    &&:link,
    &&:visited,
    &&:hover,
    &&:focus,
    &&:active {
      text-decoration: none;
      color: var(--cds-color-neutral-primary);
    }

    /* :focus-visible (not :focus) so the hover background doesn't stick after a mouse click */
    &&:hover,
    &&:focus-visible {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }

    &&[aria-current='page'],
    &&[aria-current='page']:link,
    &&[aria-current='page']:visited,
    &&[aria-current='page']:hover,
    &&[aria-current='page']:focus,
    &&[aria-current='page']:active {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
      color: var(--cds-color-neutral-primary);
    }
  `,
};

type Props = {
  item: SideNavItemType;
};

/**
 *
 * The SideNavExpandableItem component is used to render an expandable item in the sidebar.
 * @param item - The item to render
 * @returns The SideNavExpandableItem component
 */
export const SideNavExpandableItem = ({ item }: Props) => {
  const { isSideNavCollapsed, expand } = useSideNavCollapse();
  const { id, label, icon: Icon, children = [] } = item;

  const hasActiveChild = React.useMemo(() => children.some((child) => child.isActive), [children]);

  // Open by default if a child is active on mount — landing on a sub-route should reveal it.
  const [isOpen, setIsOpen] = React.useState<boolean>(hasActiveChild);

  React.useEffect(() => {
    if (isSideNavCollapsed) {
      // Collapsing the nav closes any open submenu so it doesn't pop back open on next expand.
      setIsOpen(false);
    } else if (hasActiveChild) {
      // Just expanded the nav and a child is active — reveal it.
      setIsOpen(true);
    }
  }, [isSideNavCollapsed, hasActiveChild]);

  const handleClick = useCallback<() => void>(() => {
    if (isSideNavCollapsed) {
      expand();
      setIsOpen(true);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [isSideNavCollapsed, expand]);

  // The parent button should be active if the submenu is closed and there is an active child.
  const showParentActive = !isOpen && hasActiveChild;

  if (children.length < 1) {
    return null;
  }

  const showSubmenu = !isSideNavCollapsed && isOpen && children.length > 0;

  return (
    <li css={styles.item}>
      <button
        type="button"
        onClick={handleClick}
        css={[
          styles.parentButton,
          isSideNavCollapsed && styles.parentButtonCollapsed,
          showParentActive && styles.parentButtonActive,
        ]}
        aria-expanded={showSubmenu}
        aria-controls={`sidenav-submenu-${id}`}
        aria-current={showParentActive ? 'page' : undefined}
        aria-label={label}
      >
        <SideNavToolTipWrapper content={label} isSideNavCollapsed={isSideNavCollapsed}>
          <span css={styles.icon}>
            <Icon size="medium" />
          </span>
        </SideNavToolTipWrapper>
        <Typography2
          variant={showParentActive ? 'actionSecondary' : 'bodySecondary'}
          component="span"
          color="inherit"
          css={[styles.label, isSideNavCollapsed && styles.labelHidden]}
        >
          {label}
        </Typography2>
        <span
          css={[
            styles.chevron,
            isSideNavCollapsed && styles.chevronHidden,
            isOpen ? styles.chevronOpen : styles.chevronClosed,
          ]}
        >
          <ChevronDownIcon size="small" />
        </span>
      </button>
      <ul
        id={`sidenav-submenu-${id}`}
        css={[styles.submenu, !showSubmenu && styles.submenuHidden]}
        aria-label={_t('#{label} submenu', { label })}
        aria-hidden={!showSubmenu}
      >
        {children.map((child) => (
          <li key={child.id} css={styles.item}>
            <Link to={child.href} css={styles.subitemLink} aria-current={child.isActive ? 'page' : undefined}>
              <Typography2
                variant={child.isActive ? 'actionSecondary' : 'bodySecondary'}
                component="span"
                color="inherit"
                css={styles.label}
              >
                {child.label}
              </Typography2>
            </Link>
          </li>
        ))}
      </ul>
    </li>
  );
};

export default SideNavExpandableItem;
